import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Heart, Globe2, HandHeart, Shield } from "lucide-react";
import { api } from "../lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1701020687568-4c1a40cffd89?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85";

const CAUSES = [
  {
    title: "Stray Dog Rescue",
    tag: "Crisis Response",
    desc: "Cold-weather rescues, sterilization clinics, and forever-home placement across 14 countries.",
    img: "https://images.unsplash.com/photo-1700922180830-009061498903?crop=entropy&cs=srgb&fm=jpg&w=1200",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    title: "Wildlife Protection",
    tag: "Conservation",
    desc: "Anti-poaching units & habitat preservation, from Kenyan savannahs to Indonesian rainforests.",
    img: "https://images.unsplash.com/photo-1517486430290-35657bdcef51?crop=entropy&cs=srgb&fm=jpg&w=1200",
    span: "lg:col-span-2",
  },
  {
    title: "Community Aid",
    tag: "Less Privileged",
    desc: "Food, shelter, and care for under-served communities living alongside the animals we protect.",
    img: "https://images.pexels.com/photos/6646867/pexels-photo-6646867.jpeg?auto=compress&cs=tinysrgb&w=1200",
    span: "lg:col-span-1",
  },
  {
    title: "Sanctuary Network",
    tag: "Lifelong Care",
    desc: "Funding 23 sanctuaries housing animals too injured or traumatized to be released.",
    img: "https://images.pexels.com/photos/160709/raccoon-animal-animal-world-wildlife-photography-160709.jpeg?auto=compress&cs=tinysrgb&w=1200",
    span: "lg:col-span-1",
  },
];

export default function Home() {
  const [stats, setStats] = useState({
    animals_protected: 24318,
    countries: 47,
    grants_funded: 312,
    volunteers: 5421,
  });

  useEffect(() => {
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#0A0A0C] text-white">
      {/* ============================== HERO */}
      <section
        data-testid="hero-section"
        className="relative min-h-[92vh] grain flex items-end overflow-hidden -mt-20"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/60 via-[#0A0A0C]/40 to-[#0A0A0C]" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-20 pt-40 fade-up">
          <div className="text-xs tracking-[0.35em] uppercase text-[#E85D04] mb-6">
            ◆ Est. 2018 · Global Animal Rights Foundation
          </div>
          <h1 className="font-serif-display text-6xl sm:text-7xl lg:text-[8.5rem] leading-[0.92] tracking-tighter max-w-5xl">
            Voice for the <em className="text-[#E85D04] not-italic font-serif-display">voiceless.</em>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            From rescuing stray dogs in winter alleys to funding wildlife protection across 47
            countries, the John Depp Foundation stands beside every creature — and every
            community — that the world has overlooked.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/donate"
              data-testid="hero-donate-btn"
              className="inline-flex items-center gap-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
            >
              Donate Now <ArrowRight size={16} />
            </Link>
            <Link
              to="/grants"
              data-testid="hero-grants-btn"
              className="inline-flex items-center gap-3 border border-white/25 hover:border-white text-white px-8 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
            >
              Apply For A Grant <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================== STATS */}
      <section data-testid="stats-section" className="border-y border-white/10 bg-[#0A0A0C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
          {[
            { v: stats.animals_protected.toLocaleString(), l: "Animals Protected" },
            { v: stats.countries, l: "Countries Served" },
            { v: stats.grants_funded, l: "Grants Funded" },
            { v: stats.volunteers.toLocaleString(), l: "Volunteers Worldwide" },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`stat-${i}`}
              className={`py-14 px-2 ${
                i !== 3 ? "lg:border-r border-white/10" : ""
              } ${i < 2 ? "border-b lg:border-b-0 border-white/10" : ""} ${
                i === 0 ? "border-r border-white/10" : ""
              } ${i === 2 ? "border-r lg:border-r border-white/10" : ""}`}
            >
              <div className="font-serif-display text-5xl lg:text-6xl text-white">{s.v}</div>
              <div className="mt-3 text-[10px] tracking-[0.3em] uppercase text-white/40">
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================== MISSION */}
      <section data-testid="mission-section" className="py-32 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-6">
              Our Mission
            </div>
            <h2 className="font-serif-display text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              We stand for those who cannot speak — <em className="not-italic text-[#E85D04]">and those who go unheard.</em>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-6 text-white/70 space-y-6 text-lg leading-relaxed">
            <p>
              The John Depp Foundation operates at the intersection of animal welfare and human
              dignity. Wherever animals suffer, vulnerable communities are usually suffering
              alongside them. Our work bridges the two.
            </p>
            <p>
              Through direct rescue operations, grant funding for grassroots animal lovers, and
              community aid programs, we transform compassion into measurable change — one
              shelter, one sanctuary, one life at a time.
            </p>
            <div className="grid grid-cols-2 gap-6 pt-6">
              {[
                { Icon: Heart, t: "Rescue & Rehabilitation" },
                { Icon: Globe2, t: "Worldwide Reach" },
                { Icon: HandHeart, t: "Community Aid" },
                { Icon: Shield, t: "Lifelong Sanctuary" },
              ].map(({ Icon, t }, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Icon size={18} className="text-[#E85D04]" />
                  <span className="text-white text-sm">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================== CAUSES BENTO */}
      <section data-testid="causes-section" className="bg-[#0A0A0C] border-t border-white/10 py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-4">
                Causes We Fund
              </div>
              <h2 className="font-serif-display text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                Four pillars. <br /> One mission.
              </h2>
            </div>
            <Link
              to="/causes"
              className="text-sm tracking-[0.2em] uppercase text-white/70 hover:text-[#E85D04] flex items-center gap-2"
              data-testid="causes-view-all"
            >
              View All Programs <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 lg:grid-rows-2 gap-4 lg:h-[640px]">
            {CAUSES.map((c, i) => (
              <article
                key={c.title}
                data-testid={`cause-card-${i}`}
                className={`relative group overflow-hidden border border-white/10 ${c.span} min-h-[300px]`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${c.img})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-transparent" />
                <div className="relative h-full p-8 flex flex-col justify-end">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#E85D04] mb-3">
                    {c.tag}
                  </div>
                  <h3 className="font-serif-display text-3xl lg:text-4xl leading-tight">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-white/70 text-sm max-w-md">{c.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== GRANT CTA */}
      <section data-testid="grant-cta-section" className="border-t border-white/10 py-32 bg-[#121215]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.3em] uppercase text-[#E85D04] mb-6">
              Grant Program
            </div>
            <h2 className="font-serif-display text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Animal lover with a vision? <br />
              <em className="not-italic text-[#E85D04]">We'll help fund it.</em>
            </h2>
            <p className="mt-6 text-white/70 text-lg max-w-2xl leading-relaxed">
              Whether you're running a backyard rescue, building a sanctuary, or piloting a
              spay/neuter program — our grants of $500 to $25,000 give grassroots animal lovers
              the resources to do more.
            </p>
            <Link
              to="/grants"
              data-testid="grant-cta-button"
              className="mt-10 inline-flex items-center gap-3 bg-[#E85D04] hover:bg-[#DC2F02] text-white px-8 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
            >
              Apply for a Grant <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 gap-4">
            {[
              { t: "$500 – $5,000", l: "Starter Grants — for individual rescuers and small shelters." },
              { t: "$5,000 – $25,000", l: "Program Grants — for established groups scaling impact." },
              { t: "Rolling", l: "Applications reviewed quarterly. Decisions within 60 days." },
            ].map((b, i) => (
              <div key={i} className="border border-white/10 p-6">
                <div className="font-serif-display text-2xl text-[#E85D04]">{b.t}</div>
                <div className="mt-2 text-white/70 text-sm">{b.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================== MARQUEE */}
      <section className="border-y border-white/10 py-10 overflow-hidden bg-[#0A0A0C]">
        <div className="flex gap-16 whitespace-nowrap animate-marquee">
          {Array(2).fill(0).map((_, k) => (
            <div key={k} className="flex gap-16 items-center">
              {["Kenya", "Romania", "Philippines", "India", "Mexico", "Greece", "Indonesia", "Brazil", "Ukraine", "Vietnam"].map((c) => (
                <span key={`${k}-${c}`} className="font-serif-display text-4xl text-white/30">
                  {c} <span className="text-[#E85D04]">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
